import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { hexToBytes, numbersToBytes, bytesToNumbers, parseJsonArrayToBytes } from '@/utils/hex.utils';
import { addFile, getFile } from '@/utils/fileOperations';
import { SIX_TOPIC } from '@/config/networkConstants';
import {
  startFacilitation,
  fileKeyProcessed,
  fileKeyFailed,
  facilitationComplete,
} from '@/store/slices/fileKey/fileKeyFacilitationSlice';
import { setDecapsKey } from '@/store/slices/auth/authSlice';
import { trackPendingAck } from '@/store/slices/poc/messagingSlice';
import type { FileNeedingNewKey, MissingFileKeyHelpRequest } from '@/types/fileKey/FileKeyFacilitation';
import type { MessagePayload } from '@/types/poc/message';
import type { HeliaNode } from '@/types/poc/helia';
import type { LibP2pWithServices } from '@/types/poc/libp2p';
import type { PocServices } from '@/types/shared/services.types';
import type { AppDispatch } from '@/store';
import type { Logger } from '@/utils/logger';

const HEX_BASE = 16;
const HEX_PAD_LENGTH = 2;
const ENCRYPTION_FETCH_TIMEOUT_MS = 30_000;

/**
 * Shape of the encrypted file key blob stored in Helia.
 * This matches HAP's buildEncryptedFileKeys output — NO recipientId in the blob.
 */
interface HeliaFileKeyBlob {
  cipherText: number[];
  encryptedFileKey: {
    encryptedData: number[];
    iv: number[];
    authTag: number[];
  };
}

interface FacilitationContext {
  facilitatorPrivateKey: Uint8Array;
  currentUserId: string;
  dispatch: AppDispatch;
  pocServices: PocServices;
  log: Logger;
}

/**
 * Orchestration thunk: fetches missing file key requests from the API,
 * re-encrypts each file key for each requestor, stores the new key in Helia,
 * and publishes a gossipsub message to notify HAP.
 *
 * Fire-and-forget: dispatched on page load after login.
 */
export const facilitateFileKeys = createAppAsyncThunk(
  'fileKeyFacilitation/facilitate',
  async(_, { dispatch, getState, extra, rejectWithValue }) => {
    const { pocServices, userService, fileKeyFacilitationService, loggerService } = extra;
    const log = loggerService.createLogger({ namespace: 'six3ro:file-key-facilitation' });

    try {
      // Step 1: Fetch missing file key requests
      log.info('Checking for missing file key requests...');
      const requests = await fileKeyFacilitationService.getMissingFileKeyRequests();

      if (requests.length === 0) {
        log.info('No missing file key requests. Nothing to facilitate.');
        return { processed: 0, total: 0 };
      }

      // Count total files across all requestors
      const totalFiles = requests.reduce(
        (sum, req) => sum + req.filesNeedingNewFileKey.length,
        0
      );
      log.info(`Found ${requests.length} requestor(s), ${totalFiles} file(s) to facilitate.`);
      dispatch(startFacilitation({ totalCount: totalFiles }));

      // Step 2: Get facilitator's decrypted private key
      const state = getState();
      const currentUserId = state.user.user?.id;
      if (currentUserId == null) {
        throw new Error('User not authenticated');
      }

      // eslint-disable-next-line @typescript-eslint/init-declarations -- assigned in both branches below
      let facilitatorPrivateKey: Uint8Array;

      if (state.auth.decapsKey.length > 0) {
        log.debug('Using cached private key');
        facilitatorPrivateKey = hexToBytes(state.auth.decapsKey);
      } else {
        log.debug('No cached key, fetching and decrypting...');
        const ukek = state.auth.ukek;
        if (ukek.length === 0) {
          throw new Error('UKEK not available for private key decryption');
        }

        const { decapsKey: encryptedPrivateKeyHex } = await userService.getDecapsKey(currentUserId);
        const ukekBytes = hexToBytes(ukek);
        facilitatorPrivateKey = await pocServices.cryptoService.decryptPrivateKey(
          encryptedPrivateKeyHex,
          ukekBytes
        );

        // Cache for future use
        const decryptedPrivateKeyHex = Array.from(facilitatorPrivateKey)
          .map(b => b.toString(HEX_BASE).padStart(HEX_PAD_LENGTH, '0'))
          .join('');
        dispatch(setDecapsKey(decryptedPrivateKeyHex));
        log.debug('Private key decrypted and cached');
      }

      // Step 3: Process each requestor sequentially
      const ctx: FacilitationContext = {
        facilitatorPrivateKey,
        currentUserId,
        dispatch,
        pocServices,
        log,
      };

      for (const request of requests) {
        await processRequestor(request, ctx);
      }

      dispatch(facilitationComplete());
      const finalState = getState();
      const result = {
        processed: finalState.fileKeyFacilitation.processedCount,
        total: totalFiles,
        errors: finalState.fileKeyFacilitation.errors,
      };
      log.info('File key facilitation complete:', result);
      return result;

    } catch (error) {
      log.error('File key facilitation failed:', error);
      dispatch(facilitationComplete());
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown facilitation error'
      );
    }
  }
);

/**
 * Process all files for a single requestor.
 */
async function processRequestor(
  request: MissingFileKeyHelpRequest,
  ctx: FacilitationContext
): Promise<void> {
  const { requestor, filesNeedingNewFileKey } = request;

  const recipientPublicKey = parseJsonArrayToBytes(requestor.encapsKey);
  ctx.log.debug(`Processing ${filesNeedingNewFileKey.length} file(s) for requestor: ${requestor.userId}`);

  for (const file of filesNeedingNewFileKey) {
    try {
      await processOneFileKey(file, recipientPublicKey, requestor.userId, ctx);
      ctx.dispatch(fileKeyProcessed());
    } catch (fileError) {
      const errorMsg = `Failed for file ${file.fileId} (six: ${file.sixId}): ${fileError instanceof Error ? fileError.message : 'Unknown error'}`;
      ctx.log.error(errorMsg);
      ctx.dispatch(fileKeyFailed(errorMsg));
    }
  }
}

/**
 * Re-encrypt a single file key for a single requestor, store in Helia, and publish gossipsub.
 */
async function processOneFileKey(
  file: FileNeedingNewKey,
  recipientPublicKey: Uint8Array,
  requestorUserId: string,
  ctx: FacilitationContext
): Promise<void> {
  const { facilitatorPrivateKey, currentUserId, pocServices, log } = ctx;
  const { p2pStackManagerService, messagingService, fileEncryptionService } = pocServices;

  // 1. Get Helia node for this six
  const helia: HeliaNode | null = p2pStackManagerService.getHeliaNode(file.sixId);
  if (helia === null) {
    throw new Error(`Helia node not initialized for six: ${file.sixId}`);
  }

  // 2. Fetch facilitator's encrypted file key from Helia by CID
  log.debug(`Fetching facilitator file key CID: ${file.facilitatorFileKeyCID}`);
  const encryptionJsonBytes = await getFile(helia, file.facilitatorFileKeyCID, {
    signal: AbortSignal.timeout(ENCRYPTION_FETCH_TIMEOUT_MS),
  });
  const encryptionJsonString = new TextDecoder().decode(new Uint8Array(encryptionJsonBytes));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- JSON parsed from trusted Helia blob
  const blob = JSON.parse(encryptionJsonString) as HeliaFileKeyBlob;

  // 3. Convert number arrays to Uint8Arrays for the crypto service
  const facilitatorEncryption = {
    recipientId: currentUserId,
    cipherText: numbersToBytes(blob.cipherText),
    encryptedFileKey: {
      encryptedData: numbersToBytes(blob.encryptedFileKey.encryptedData),
      iv: numbersToBytes(blob.encryptedFileKey.iv),
      authTag: numbersToBytes(blob.encryptedFileKey.authTag),
    },
  };

  // 4. Re-encrypt the file key for the new recipient
  log.debug(`Re-encrypting file key for requestor: ${requestorUserId}, file: ${file.fileId}`);
  const reEncrypted = await fileEncryptionService.reEncryptFileKeyForRecipient(
    facilitatorEncryption,
    facilitatorPrivateKey,
    recipientPublicKey
  );

  // 5. Serialize new encrypted file key to JSON and store in Helia
  const newBlob: HeliaFileKeyBlob = {
    cipherText: bytesToNumbers(reEncrypted.cipherText),
    encryptedFileKey: {
      encryptedData: bytesToNumbers(reEncrypted.encryptedFileKey.encryptedData),
      iv: bytesToNumbers(reEncrypted.encryptedFileKey.iv),
      authTag: bytesToNumbers(reEncrypted.encryptedFileKey.authTag),
    },
  };
  const blobBytes = new TextEncoder().encode(JSON.stringify(newBlob));
  const blobFile = new File([blobBytes], `filekey-${file.fileId}-${requestorUserId}.json`, {
    type: 'application/octet-stream',
  });

  const addResult = await addFile(helia, blobFile);
  const newFileKeyCID = addResult.cid.toString();
  log.debug(`Stored new file key in Helia, CID: ${newFileKeyCID}`);

  // 6. Publish gossipsub message to notify HAP
  const libp2pNode: LibP2pWithServices | null = p2pStackManagerService.getLibp2pNode(file.sixId);
  if (libp2pNode == null) {
    throw new Error(`LibP2P node not available for six: ${file.sixId}`);
  }

  const message: MessagePayload = {
    type: 'file-key-ready-for-download',
    id: crypto.randomUUID(),
    fileId: file.fileId,
    newFileKeyCID,
    newFileKeyForUserId: requestorUserId,
    newFileKeyCreatorUserId: currentUserId,
    sixId: file.sixId,
    sender: libp2pNode.peerId.toString(),
    timestamp: Date.now(),
  };

  await messagingService.publishMessage(
    libp2pNode,
    SIX_TOPIC(file.sixId),
    message
  );

  // 7. Track pending ack with retry
  trackPendingAck(
    message.id,
    { sixId: file.sixId, cid: newFileKeyCID, metadata: { name: `filekey-${file.fileId}` } },
    () => {
      void messagingService.publishMessage(libp2pNode, SIX_TOPIC(file.sixId), {
        ...message,
        id: crypto.randomUUID(),
        timestamp: Date.now(),
      });
    }
  );

  log.info(`Published file-key-ready-for-download for file: ${file.fileId}, requestor: ${requestorUserId}, CID: ${newFileKeyCID}`);
}
